import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Avatar, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, InputAdornment, Tab, Tabs, FormControlLabel,
  Switch, Autocomplete
} from '@mui/material';
import {
  School, Add, Edit, Delete, Search, Star, EmojiEvents,
  Work, Person, GroupAdd, VolunteerActivism, Event,
  TrendingUp, FileDownload, Bolt, Verified, Leaderboard
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const STATUS_CFG = {
  employed:      { label: 'Employed',       color: 'success', icon: <Work sx={{ fontSize: 14 }} /> },
  self_employed: { label: 'Self-Employed',  color: 'success', icon: <Work sx={{ fontSize: 14 }} /> },
  university:    { label: 'University',     color: 'primary', icon: <School sx={{ fontSize: 14 }} /> },
  college:       { label: 'College',        color: 'primary', icon: <School sx={{ fontSize: 14 }} /> },
  gap_year:      { label: 'Gap Year',       color: 'warning', icon: <Person sx={{ fontSize: 14 }} /> },
  unknown:       { label: 'Unknown',        color: 'default', icon: <Person sx={{ fontSize: 14 }} /> },
};
const EXIT_TYPES = ['graduated','completed','transferred','other'];
const EVENT_TYPES = ['reunion','mentorship','career_talk','donation_drive','other'];
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const fmt  = n => `E${parseFloat(n||0).toLocaleString('en', { minimumFractionDigits: 2 })}`;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Overview / Stats Tab ───────────────────────────────────────────────────────
function StatsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/alumni/stats')
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  if (loading) return <CircularProgress />;
  if (!stats) return null;
  const { summary, top_academic, top_donors, by_year } = stats;

  return (
    <Box>
      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Alumni',    value: summary?.total_alumni || 0,  color: '#0f2b4a', icon: <School /> },
          { label: 'Employed',        value: summary?.employed || 0,       color: '#2e7d32', icon: <Work /> },
          { label: 'In Education',    value: summary?.in_education || 0,   color: '#1565c0', icon: <School /> },
          { label: 'Mentors',         value: summary?.mentors || 0,        color: '#6a1b9a', icon: <Verified /> },
          { label: 'Grad. Years',     value: summary?.graduation_years || 0, color: '#e65100', icon: <EmojiEvents /> },
          { label: 'Total Donated',   value: fmt(summary?.total_donated),  color: '#c9952a', icon: <VolunteerActivism /> },
        ].map(s => (
          <Grid item xs={6} sm={4} md={2} key={s.label}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${s.color}` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{s.label}</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {/* Academic leaderboard */}
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEvents color="warning" /> Top Academic Performers
              </Typography>
              {!top_academic?.length && <Typography fontSize={12} color="text.secondary">No data yet</Typography>}
              {top_academic?.map((a, i) => (
                <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography fontWeight={800} fontSize={18} sx={{ color: i === 0 ? '#c9952a' : i === 1 ? '#aaa' : i === 2 ? '#cd7f32' : '#ccc', minWidth: 24 }}>#{i+1}</Typography>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: '#0f2b4a' }}>
                    {a.first_name?.[0]}{a.last_name?.[0]}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontSize={13} fontWeight={600}>{a.first_name} {a.last_name}</Typography>
                    <Typography fontSize={10} color="text.secondary">{a.graduation_year} · {a.graduation_class}</Typography>
                  </Box>
                  {a.avg_score && <Chip label={`${a.avg_score}%`} size="small" color="success" sx={{ fontWeight: 700 }} />}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Top donors */}
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VolunteerActivism color="error" /> Top Donors
              </Typography>
              {!top_donors?.length && <Typography fontSize={12} color="text.secondary">No donations recorded yet</Typography>}
              {top_donors?.map((a, i) => (
                <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography fontWeight={800} fontSize={18} sx={{ color: i === 0 ? '#c9952a' : '#ccc', minWidth: 24 }}>#{i+1}</Typography>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontSize={13} fontWeight={600}>{a.first_name} {a.last_name}</Typography>
                    <Typography fontSize={10} color="text.secondary">Class of {a.graduation_year} · {a.donation_count} donation(s)</Typography>
                  </Box>
                  <Chip label={fmt(a.total_donated)} size="small" color="warning" sx={{ fontWeight: 700 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* By graduation year */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>Alumni by Graduation Year</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Year</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Alumni</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Employed</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Employment Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {by_year?.map(y => {
                      const rate = y.count > 0 ? Math.round((y.employed / y.count) * 100) : 0;
                      return (
                        <TableRow key={y.graduation_year} hover>
                          <TableCell sx={{ fontWeight: 700 }}>Class of {y.graduation_year}</TableCell>
                          <TableCell align="center"><Chip label={y.count} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                          <TableCell align="center"><Chip label={y.employed} size="small" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flexGrow: 1, height: 6, bgcolor: '#e0e0e0', borderRadius: 3 }}>
                                <Box sx={{ width: `${rate}%`, height: '100%', bgcolor: rate >= 70 ? '#2e7d32' : rate >= 40 ? '#e65100' : '#c62828', borderRadius: 3 }} />
                              </Box>
                              <Typography fontSize={11} fontWeight={700}>{rate}%</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!by_year?.length && <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>No alumni records yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Alumni Directory Tab ───────────────────────────────────────────────────────
function DirectoryTab({ students, academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [alumni, setAlumni]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [filterYear, setYear]       = useState('');
  const [filterStatus, setStatus]   = useState('');
  const [filterMentor, setMentor]   = useState(false);
  const [dialog, setDialog]         = useState(false);
  const [editing, setEditing]       = useState(null);
  const [promoteDialog, setPromote] = useState(false);
  const [bulkDialog, setBulk]       = useState(false);
  const [detailId, setDetail]       = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [donateDialog, setDonate]   = useState(null);

  const PROFILE_BLANK = { current_status:'unknown', current_employer:'', current_position:'',
    university_name:'', course_studied:'', personal_email:'', personal_phone:'',
    current_address:'', is_mentor: false, mentor_subjects:'', post_school_achievements:'', notes:'' };
  const [profileForm, setProfileForm] = useState(PROFILE_BLANK);
  const [promoteForm, setPromoteForm] = useState({ student_id:'', graduation_year: new Date().getFullYear(), graduation_class:'', exit_type:'graduated', notes:'' });
  const [bulkForm, setBulkForm]       = useState({ academic_year_id:'', class_id:'', graduation_year: new Date().getFullYear() });
  const [donateForm, setDonateForm]   = useState({ amount:'', donation_date: new Date().toISOString().split('T')[0], purpose:'', notes:'' });
  const [classes, setClasses]         = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/alumni', { params: { search: search||undefined, graduation_year: filterYear||undefined, current_status: filterStatus||undefined, is_mentor: filterMentor?'true':undefined } });
      if (r.data.success) setAlumni(r.data.data);
    } catch { enqueueSnackbar('Failed to load alumni', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [search, filterYear, filterStatus, filterMentor, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {}); }, []);

  const openEdit = async (a) => {
    setEditing(a);
    setProfileForm({ current_status: a.current_status||'unknown', current_employer: a.current_employer||'',
      current_position: a.current_position||'', university_name: a.university_name||'',
      course_studied: a.course_studied||'', personal_email: a.personal_email||'',
      personal_phone: a.personal_phone||'', current_address: a.current_address||'',
      is_mentor: !!a.is_mentor, mentor_subjects: a.mentor_subjects||'',
      post_school_achievements: a.post_school_achievements||'', notes: a.notes||'' });
    setDialog(true);
  };

  const saveProfile = async () => {
    try {
      await api.put(`/alumni/${editing.id}`, profileForm);
      enqueueSnackbar('Profile updated', { variant: 'success' });
      setDialog(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const promote = async () => {
    try {
      const r = await api.post('/alumni/promote', promoteForm);
      enqueueSnackbar(r.data.message, { variant: 'success' });
      setPromote(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const bulkPromote = async () => {
    try {
      const r = await api.post('/alumni/bulk-promote', bulkForm);
      enqueueSnackbar(r.data.message, { variant: 'success' });
      setBulk(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const openDetail = async (id) => {
    setDetail(id);
    const r = await api.get(`/alumni/${id}`);
    if (r.data.success) setDetailData(r.data.data);
  };

  const addDonation = async () => {
    try {
      await api.post('/alumni/donations', { alumni_id: donateDialog, ...donateForm });
      enqueueSnackbar('Donation recorded', { variant: 'success' });
      setDonate(null); openDetail(donateDialog); load();
    } catch (err) { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this alumni record?')) return;
    try { await api.delete(`/alumni/${id}`); enqueueSnackbar('Removed', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const gradYears = [...new Set(alumni.map(a => a.graduation_year))].sort((a,b) => b-a);

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="Search name, employer, university…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 250 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Graduation Year" value={filterYear} onChange={e => setYear(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">All years</MenuItem>
          {gradYears.map(y => <MenuItem key={y} value={y}>Class of {y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATUS_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch size="small" checked={filterMentor} onChange={e => setMentor(e.target.checked)} />}
          label={<Typography fontSize={12}>Mentors only</Typography>} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() => exportCSV('alumni',
          ['Name', 'Admission', 'Year', 'Class', 'Status', 'Employer/University', 'Mentor', 'Total Donated'],
          alumni.map(a => [`${a.first_name} ${a.last_name}`, a.admission_number, a.graduation_year, a.graduation_class||'',
            STATUS_CFG[a.current_status]?.label||'', a.current_employer||a.university_name||'',
            a.is_mentor?'Yes':'No', fmt(a.total_donated)])
        )}>Export</Button>
        <Button variant="outlined" size="small" startIcon={<Bolt />} onClick={() => setBulk(true)}>Bulk Promote</Button>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setPromote(true)} sx={{ bgcolor: '#0f2b4a' }}>Register Alumni</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {alumni.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                <School sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No alumni records yet. Register graduates to get started.</Typography>
              </Paper>
            </Grid>
          )}
          {alumni.map(a => {
            const cfg = STATUS_CFG[a.current_status] || STATUS_CFG.unknown;
            return (
              <Grid item xs={12} sm={6} md={4} key={a.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                  onClick={() => openDetail(a.id)}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar sx={{ bgcolor: '#0f2b4a', width: 44, height: 44, fontSize: 16 }}>
                        {a.first_name?.[0]}{a.last_name?.[0]}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} fontSize={14} noWrap>{a.first_name} {a.last_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{a.admission_number} · Class of {a.graduation_year}</Typography>
                        {a.graduation_class && <Typography fontSize={10} color="text.secondary">{a.graduation_class}</Typography>}
                      </Box>
                      <Stack direction="row" spacing={0.3} onClick={e => e.stopPropagation()}>
                        <Tooltip title="Edit profile"><IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Record donation"><IconButton size="small" color="warning" onClick={() => { setDonate(a.id); setDonateForm({ amount:'', donation_date: new Date().toISOString().split('T')[0], purpose:'', notes:'' }); }}><VolunteerActivism fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => del(a.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                      <Chip icon={cfg.icon} label={cfg.label} size="small" color={cfg.color} sx={{ fontSize: 10 }} />
                      {a.is_mentor && <Chip icon={<Verified sx={{ fontSize: 12 }} />} label="Mentor" size="small" color="secondary" sx={{ fontSize: 10 }} />}
                      {a.total_donated > 0 && <Chip icon={<VolunteerActivism sx={{ fontSize: 12 }} />} label={fmt(a.total_donated)} size="small" color="warning" sx={{ fontSize: 10 }} />}
                    </Stack>
                    {(a.current_employer || a.university_name) && (
                      <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                        {a.current_employer || a.university_name}
                        {a.current_position && ` · ${a.current_position}`}
                        {a.course_studied && ` · ${a.course_studied}`}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>Update Alumni Profile — {editing?.first_name} {editing?.last_name}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Current Status" value={profileForm.current_status} onChange={e => setProfileForm(f => ({ ...f, current_status: e.target.value }))}>
                {Object.entries(STATUS_CFG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Employer / Company" value={profileForm.current_employer} onChange={e => setProfileForm(f => ({ ...f, current_employer: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Position / Role" value={profileForm.current_position} onChange={e => setProfileForm(f => ({ ...f, current_position: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="University / College" value={profileForm.university_name} onChange={e => setProfileForm(f => ({ ...f, university_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Course / Degree" value={profileForm.course_studied} onChange={e => setProfileForm(f => ({ ...f, course_studied: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Personal Email" value={profileForm.personal_email} onChange={e => setProfileForm(f => ({ ...f, personal_email: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Personal Phone" value={profileForm.personal_phone} onChange={e => setProfileForm(f => ({ ...f, personal_phone: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Current Address" value={profileForm.current_address} onChange={e => setProfileForm(f => ({ ...f, current_address: e.target.value }))} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Post-school Achievements" value={profileForm.post_school_achievements} onChange={e => setProfileForm(f => ({ ...f, post_school_achievements: e.target.value }))} multiline rows={2} placeholder="Awards, notable achievements, publications, etc." />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={profileForm.is_mentor} onChange={e => setProfileForm(f => ({ ...f, is_mentor: e.target.checked }))} />}
                label={<Typography fontSize={13}>Available as School Mentor</Typography>} />
            </Grid>
            {profileForm.is_mentor && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Subjects / Areas willing to mentor" value={profileForm.mentor_subjects} onChange={e => setProfileForm(f => ({ ...f, mentor_subjects: e.target.value }))} placeholder="e.g. Mathematics, Career Guidance, Entrepreneurship" />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Internal Notes" value={profileForm.notes} onChange={e => setProfileForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveProfile} sx={{ bgcolor: '#0f2b4a' }}>Save Profile</Button>
        </DialogActions>
      </Dialog>

      {/* Register Single Alumni Dialog */}
      <Dialog open={promoteDialog} onClose={() => setPromote(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register Graduate as Alumni</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={students}
                getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                onChange={(_, v) => setPromoteForm(f => ({ ...f, student_id: v?.id || '' }))}
                renderInput={p => <TextField {...p} label="Select Student *" />} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Graduation Year *" value={promoteForm.graduation_year} onChange={e => setPromoteForm(f => ({ ...f, graduation_year: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Exit Type" value={promoteForm.exit_type} onChange={e => setPromoteForm(f => ({ ...f, exit_type: e.target.value }))}>
                {EXIT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Graduation Class (optional)" value={promoteForm.graduation_class} onChange={e => setPromoteForm(f => ({ ...f, graduation_class: e.target.value }))} placeholder="e.g. Form 5 A" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromote(false)}>Cancel</Button>
          <Button variant="contained" onClick={promote} sx={{ bgcolor: '#0f2b4a' }}>Register as Alumni</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Promote Dialog */}
      <Dialog open={bulkDialog} onClose={() => setBulk(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Promote Students to Alumni</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>Promotes all active students in the selected academic year (and optionally class) to alumni. Student status will be set to Graduated.</Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Academic Year *" value={bulkForm.academic_year_id} onChange={e => setBulkForm(f => ({ ...f, academic_year_id: e.target.value }))}>
                <MenuItem value="">Select year…</MenuItem>
                {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Class (optional)" value={bulkForm.class_id} onChange={e => setBulkForm(f => ({ ...f, class_id: e.target.value }))}>
                <MenuItem value="">All classes</MenuItem>
                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Graduation Year *" value={bulkForm.graduation_year} onChange={e => setBulkForm(f => ({ ...f, graduation_year: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulk(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={bulkPromote} startIcon={<Bolt />}>Promote All</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailId} onClose={() => { setDetail(null); setDetailData(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>Alumni Profile</DialogTitle>
        <DialogContent dividers>
          {!detailData ? <CircularProgress /> : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ width: 52, height: 52, bgcolor: '#0f2b4a', fontSize: 20 }}>{detailData.first_name?.[0]}{detailData.last_name?.[0]}</Avatar>
                  <Box>
                    <Typography fontWeight={700} fontSize={16}>{detailData.first_name} {detailData.last_name}</Typography>
                    <Typography fontSize={12} color="text.secondary">Class of {detailData.graduation_year} · {detailData.graduation_class}</Typography>
                    <Chip label={STATUS_CFG[detailData.current_status]?.label} size="small" color={STATUS_CFG[detailData.current_status]?.color} sx={{ mt: 0.3 }} />
                  </Box>
                </Stack>
              </Grid>
              {detailData.current_employer && <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Employer</Typography><Typography fontSize={13}>{detailData.current_employer}</Typography>{detailData.current_position && <Typography fontSize={11} color="text.secondary">{detailData.current_position}</Typography>}</Grid>}
              {detailData.university_name && <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">University</Typography><Typography fontSize={13}>{detailData.university_name}</Typography>{detailData.course_studied && <Typography fontSize={11} color="text.secondary">{detailData.course_studied}</Typography>}</Grid>}
              {detailData.post_school_achievements && <Grid item xs={12}><Typography variant="caption" color="text.secondary" display="block">Achievements</Typography><Typography fontSize={13}>{detailData.post_school_achievements}</Typography></Grid>}
              {detailData.is_mentor && <Grid item xs={12}><Chip icon={<Verified />} label={`Mentor: ${detailData.mentor_subjects || 'General'}`} color="secondary" /></Grid>}
              <Grid item xs={12}><Divider /></Grid>
              {/* Donations */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography fontWeight={700} fontSize={13}>Donations ({detailData.donations?.length || 0})</Typography>
                  <Button size="small" onClick={() => { setDonate(detailId); setDonateForm({ amount:'', donation_date: new Date().toISOString().split('T')[0], purpose:'', notes:'' }); }}>+ Add</Button>
                </Box>
                {detailData.donations?.map(d => (
                  <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                    <Typography fontSize={12}>{fmtD(d.donation_date)} · {d.purpose || 'General'}</Typography>
                    <Chip label={fmt(d.amount)} size="small" color="warning" sx={{ fontWeight: 700 }} />
                  </Box>
                ))}
                {!detailData.donations?.length && <Typography fontSize={12} color="text.secondary">No donations recorded</Typography>}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetail(null); setDetailData(null); }}>Close</Button>
          {detailData && <Button variant="outlined" onClick={() => { openEdit(detailData); setDetail(null); }}>Edit Profile</Button>}
        </DialogActions>
      </Dialog>

      {/* Donation Dialog */}
      <Dialog open={!!donateDialog} onClose={() => setDonate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Donation</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Amount (E) *" value={donateForm.amount} onChange={e => setDonateForm(f => ({ ...f, amount: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Date *" value={donateForm.donation_date} onChange={e => setDonateForm(f => ({ ...f, donation_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Purpose" value={donateForm.purpose} onChange={e => setDonateForm(f => ({ ...f, purpose: e.target.value }))} placeholder="e.g. Library fund, Bursary" /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={donateForm.notes} onChange={e => setDonateForm(f => ({ ...f, notes: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDonate(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={addDonation} startIcon={<VolunteerActivism />}>Record Donation</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Leaderboard Tab ────────────────────────────────────────────────────────────
function LeaderboardTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData]   = useState([]);
  const [year, setYear]   = useState('');
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState([]);

  useEffect(() => {
    api.get('/alumni/leaderboard').then(r => {
      if (r.data.success) {
        setData(r.data.data);
        const ys = [...new Set(r.data.data.map(a => a.graduation_year))].sort((a,b) => b-a);
        setYears(ys);
      }
    }).catch(() => enqueueSnackbar('Failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  const filtered = year ? data.filter(a => a.graduation_year == year) : data;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
        🌐 This leaderboard is publicly accessible at <strong>/alumni/leaderboard</strong> — no login required. Personal contact details are not shown.
      </Alert>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField select size="small" label="Filter by Year" value={year} onChange={e => setYear(e.target.value)} sx={{ width: 180 }}>
          <MenuItem value="">All Years</MenuItem>
          {years.map(y => <MenuItem key={y} value={y}>Class of {y}</MenuItem>)}
        </TextField>
        <Chip label={`${filtered.length} alumni`} size="small" />
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {filtered.map((a, i) => {
            const cfg = STATUS_CFG[a.current_status] || STATUS_CFG.unknown;
            return (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card variant="outlined" sx={{ borderRadius: 2, border: i < 3 ? `2px solid ${i===0?'#c9952a':i===1?'#aaa':'#cd7f32'}` : undefined }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      {i < 3 && <EmojiEvents sx={{ color: i===0?'#c9952a':i===1?'#9e9e9e':'#cd7f32', fontSize: 20 }} />}
                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#0f2b4a', fontSize: 14 }}>
                        {a.first_name?.[0]}{a.last_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={700} fontSize={13}>{a.first_name} {a.last_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">Class of {a.graduation_year} · {a.graduation_class}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      <Chip icon={cfg.icon} label={cfg.label} size="small" color={cfg.color} sx={{ fontSize: 10 }} />
                      {a.is_mentor && <Chip icon={<Verified sx={{ fontSize: 12 }} />} label="Mentor" size="small" color="secondary" sx={{ fontSize: 10 }} />}
                      {a.avg_score && <Chip icon={<Star sx={{ fontSize: 12 }} />} label={`${a.avg_score}% avg`} size="small" color="warning" sx={{ fontSize: 10 }} />}
                    </Stack>
                    {(a.current_employer || a.university_name) && (
                      <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                        {a.current_employer || a.university_name}
                        {(a.current_position || a.course_studied) && ` · ${a.current_position || a.course_studied}`}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {!filtered.length && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                <Leaderboard sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No alumni data for the selected year</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}

// ── Events Tab ─────────────────────────────────────────────────────────────────
function EventsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [events, setEvents]   = useState([]);
  const [dialog, setDialog]   = useState(false);
  const [form, setForm]       = useState({ title:'', event_type:'reunion', event_date:'', description:'', venue:'' });

  useEffect(() => {
    api.get('/alumni-events').then(r => { if (r.data.success) setEvents(r.data.data); }).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.post('/alumni-events', form);
      enqueueSnackbar('Event created', { variant: 'success' });
      setDialog(false);
      api.get('/alumni-events').then(r => { if (r.data.success) setEvents(r.data.data); });
    } catch (err) { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }} alignItems="center">
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)} sx={{ bgcolor: '#0f2b4a' }}>Create Event</Button>
      </Stack>

      {events.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Event sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No alumni events yet</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {events.map(e => (
            <Grid item xs={12} sm={6} md={4} key={e.id}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Chip label={e.event_type?.replace('_',' ')} size="small" color="primary" sx={{ fontSize: 10, textTransform: 'capitalize', mb: 1 }} />
                    <Chip label={`${e.attendees} attended`} size="small" sx={{ fontSize: 10 }} />
                  </Stack>
                  <Typography fontWeight={700} fontSize={14}>{e.title}</Typography>
                  <Typography fontSize={12} color="text.secondary">{fmtD(e.event_date)}{e.venue ? ` · ${e.venue}` : ''}</Typography>
                  {e.description && <Typography fontSize={12} sx={{ mt: 0.5 }} color="text.secondary">{e.description}</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Alumni Event</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={8}><TextField fullWidth size="small" label="Event Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Type" value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                {EVENT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.replace('_',' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Event Date *" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Venue" value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={3} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function Alumni() {
  const [tab, setTab]           = useState(0);
  const [students, setStudents] = useState([]);
  const [academicYears, setAY]  = useState([]);

  useEffect(() => {
    import('../services/api').then(({ studentsAPI, settingsAPI: sAPI }) => {
      studentsAPI.getAll({ limit: 2000 }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
      sAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEvents color="warning" /> Alumni Module
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Graduate tracking · Career updates · Leaderboards · Events · Donations
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<TrendingUp fontSize="small" />} label="Overview"   iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Person fontSize="small" />}     label="Directory"  iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Leaderboard fontSize="small" />} label="Leaderboard" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Event fontSize="small" />}      label="Events"     iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><StatsTab /></TabPanel>
      <TabPanel value={tab} index={1}><DirectoryTab students={students} academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={2}><LeaderboardTab /></TabPanel>
      <TabPanel value={tab} index={3}><EventsTab /></TabPanel>
    </Box>
  );
}
